/**
 * Reusable Telegram Stars payment hook.
 *
 * Flow:
 *   1. POST /v1/payments/invoice → get invoice_link
 *   2. Telegram.WebApp.openInvoice(link, callback)
 *   3. callback fires with 'paid' | 'cancelled' | 'failed' | 'pending'
 *
 * Local dev fallback: opens the link in a new tab (payment won't be
 * detected — that's expected outside Telegram).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { createInvoice } from '@/api/client';

export function usePayment(productId: string) {
  const { t } = useTranslation();
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);

  const pay = async (onSuccess: () => void) => {
    if (paying) return;
    setPaying(true);
    setPayError(null);

    try {
      const { invoice_link } = await createInvoice(productId);

      const tg = (window as any).Telegram?.WebApp;
      if (tg?.openInvoice) {
        tg.openInvoice(invoice_link, (status: string) => {
          setPaying(false);
          if (status === 'paid') {
            onSuccess();
          } else if (status === 'failed') {
            setPayError(t('payment.failed'));
          } else if (status === 'cancelled') {
            setPayError(t('payment.cancelled'));
          }
          // 'pending' — Telegram is still processing, wait for webhook
        });
      } else {
        // Local dev — no Telegram context
        window.open(invoice_link, '_blank');
        setPaying(false);
        setPayError('Open inside Telegram to complete payment.');
      }
    } catch (err) {
      setPayError((err as Error).message);
      setPaying(false);
    }
  };

  return { pay, paying, payError };
}
